import { cx } from '@vizit/theme';
import * as React from 'react';
import { Button } from 'react-aria-components';
import { Image } from '../image';
import { Text, TextSize, TextWeight } from '../text';
import type { CategoryCardProps } from './types';

/**
 * CategoryCardImage is an image component that is used in the CategoryCard component.
 * It is used to display the image in the CategoryCard component.
 * It is a wrapper around the Image component that adds a border and a rounded full shape.
 */
const CategoryCardImage = ({ name, url }: { name: string; url: string }) => {
  return (
    <div className="relative z-10 w-[115px] rounded-full border-2 border-solid border-black">
      <Image
        border={false}
        imageClassName="rounded-full"
        className="rounded-full"
        name={name}
        src={url}
        contain={false}
      />
    </div>
  );
};
/**
 * CategoryCard is an interactive card component that displays a category with images.
 * It features hover states and is fully accessible.
 *
 * @example
 * <CategoryCard
 *   title="Personal Care"
 *   images={[
 *     { src: '/image1.jpg', alt: 'Product 1' },
 *     { src: '/image2.jpg', alt: 'Product 2' },
 *     { src: '/image3.jpg', alt: 'Product 3' },
 *   ]}
 *   onPress={() => console.log('Category clicked')}
 * />
 */
function CategoryCard(props: CategoryCardProps, ref: React.ForwardedRef<HTMLButtonElement>) {
  const { title, images, className, onPress } = props;

  // Ensure we have at most 3 images
  const displayImages = images?.slice(0, 3);
  const [mainImage, ...sideImages] = displayImages || [];

  return (
    <Button
      ref={ref}
      onPress={onPress}
      className={cx(
        'group relative w-full overflow-hidden bg-black transition-colors hover:bg-[#212121]',
        'flex flex-col items-center gap-3 px-2.5 pb-2.5 pt-7',
        !images?.length ? 'justify-center' : 'justify-between',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black',
        'w-[293px] h-[288px]',
        className,
      )}
    >
      <div className="flex w-full flex-col items-center gap-36">
        {/* Category Title */}
        <Text size={TextSize.Xl} weight={TextWeight.Regular} className="text-white">
          {title}
        </Text>

        {/* Images */}
        <div className="relative inline-grid shrink-0">
          {/* Main center image with border */}
          {mainImage && (
            <div className="relative z-10 w-[115px]">
              <CategoryCardImage name={mainImage.alt || ''} url={mainImage.url} />
            </div>
          )}

          {/* Left side image */}
          {sideImages[0] && (
            <div className="absolute left-0 top-7 w-[115px] opacity-90" style={{ transform: 'translateX(-61.393px)' }}>
              <CategoryCardImage name={sideImages[0].alt || ''} url={sideImages[0].url} />
            </div>
          )}

          {/* Right side image */}
          {sideImages[1] && (
            <div className="absolute right-0 top-7 w-[115px] opacity-90" style={{ transform: 'translateX(61.393px)' }}>
              <CategoryCardImage name={sideImages[1].alt || ''} url={sideImages[1].url} />
            </div>
          )}
        </div>
      </div>
    </Button>
  );
}

const _CategoryCard = React.forwardRef<HTMLButtonElement, CategoryCardProps>(CategoryCard);

export { _CategoryCard as CategoryCard };
